import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { api } from "../lib/api";
import { formatPrice, imgUrl } from "../lib/format";
import { Trash, ArrowRight, ShieldCheck, WarningCircle, PenNib, Flask, ShoppingBag, CreditCard, Truck, Plus } from "@phosphor-icons/react";
import { useNoIndex } from "../lib/usePageMeta";
import { trackViewCart, trackCheckoutError } from "../lib/analytics";
import { pickRelatedProducts, slugsInCart } from "../lib/product-recommendations";
import {
  recommendForCart,
  CART_REC_HEADING,
  CART_REC_SUPPORT,
} from "../lib/cart-recommendations";
import { isCompanionAccessory } from "../lib/research-areas";
import { toast } from "sonner";

// Three-stage progress cue used across the cart review page.
function CheckoutStageCue() {
  const stages = [
    { key: "cart",     label: "Review cart",       icon: ShoppingBag, active: true },
    { key: "checkout", label: "Secure payment",    icon: CreditCard },
    { key: "delivery", label: "Tracked delivery",  icon: Truck },
  ];
  return (
    <ol
      data-testid="cart-stage-cue"
      className="mps-card p-4 sm:p-5 grid grid-cols-3 gap-2 sm:gap-4 text-center relative overflow-hidden"
      aria-label="Checkout progress"
    >
      {stages.map(({ key, label, icon: Icon, active }, i) => (
        <li
          key={key}
          data-testid={`cart-stage-${key}`}
          className={
            "relative flex flex-col items-center gap-2 py-2 " +
            (active ? "text-brand-navy" : "text-brand-muted")
          }
        >
          <div
            className={
              "h-9 w-9 rounded-full flex items-center justify-center border transition-colors " +
              (active
                ? "bg-brand-navy text-white border-brand-navy"
                : "bg-white text-brand-muted border-brand-border")
            }
          >
            <Icon size={16} weight="regular" />
          </div>
          <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em] leading-tight">
            <span className="hidden sm:inline">{`0${i + 1} · `}</span>{label}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Cart-page recommendations rail — "Commonly researched together".
 *
 * Placed above the order summary so it's the last editable step before
 * the shopper is handed off to Stripe.  Never auto-adds anything; every
 * item requires an explicit "Add" click.  Powered by
 * `lib/cart-recommendations.js` — deterministic, whole-cart aware, with
 * the owner-mandated GLP → MOTS-C / AOD-9604 / GHK-Cu / Tesamorelin
 * priority rule enforced across the entire cart.  Falls back to
 * companion diluent vials (Bacteriostatic Water / Acetic Acid Water)
 * when no rule fires and none of the recovery-family targets are in
 * the catalog.
 */
function CartRecommendations({ cartItems, onAdd }) {
  const [pool, setPool] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/products", { params: { limit: 200 } })
      .then((r) => { if (!cancelled) setPool(r.data.items || []); })
      .catch(() => { if (!cancelled) setPool([]); });
    return () => { cancelled = true; };
  }, []);

  const recs = React.useMemo(() => {
    if (!pool.length || !cartItems.length) return [];
    // Deterministic, whole-cart aware, dedupe-by-family, max 4.
    const engineRecs = recommendForCart(cartItems, pool, 4);
    if (engineRecs.length > 0) return engineRecs;
    // Fallback for cart items with no mapped compound family (e.g.
    // niche standards) — surface companion diluents first, then the
    // same-category legacy recommender.  Never mixes with engine picks.
    const excluded = slugsInCart(cartItems);
    const purchasable = pool.filter(
      (p) => p && p.active && p.ecommerce_enabled &&
             p.checkout_status === "Add to Cart" &&
             p.card_payment_allowed && p.public_price_visible &&
             (p.retail_price_cents || 0) > 0 &&
             !excluded.includes(p.slug),
    );
    const companions = purchasable.filter(isCompanionAccessory).slice(0, 2);
    const seed = cartItems[cartItems.length - 1];
    const sameCatPicks = pickRelatedProducts(
      seed, pool,
      [...excluded, ...companions.map((c) => c.slug)],
      Math.max(0, 4 - companions.length),
    );
    return [...companions, ...sameCatPicks].slice(0, 4);
  }, [pool, cartItems]);

  if (recs.length === 0) return null;

  return (
    <div className="mps-card p-6 lg:p-7" data-testid="cart-recommendations">
      <div className="mb-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-accent">
          Suggested pairings
        </div>
        <h3
          className="mt-1 font-display text-lg font-semibold text-brand-navy tracking-tight leading-snug"
          data-testid="cart-rec-heading"
        >
          {CART_REC_HEADING}
        </h3>
        <p
          className="mt-2 text-[12px] text-brand-muted leading-relaxed"
          data-testid="cart-rec-support"
        >
          {CART_REC_SUPPORT}
        </p>
      </div>
      <ul className="divide-y divide-brand-border">
        {recs.map((p) => (
          <li
            key={p.slug}
            data-testid={`cart-rec-${p.slug}`}
            className="py-3 flex items-center gap-3"
          >
            {p.thumbnail_image_url && (
              <img
                src={imgUrl(p.thumbnail_image_url)}
                alt={p.image_alt_text || p.public_display_name}
                className="w-14 h-14 object-contain bg-brand-surface border border-brand-border rounded-[10px] p-1 flex-shrink-0"
                loading="lazy"
              />
            )}
            <Link to={`/product/${p.slug}`} className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold text-brand-navy truncate hover:text-brand-accent transition-colors">
                {p.public_display_name || p.product_name}
              </div>
              <div className="text-[11px] text-brand-muted font-mono truncate">
                {[p.strength, p.public_sku].filter(Boolean).join(" · ")}
              </div>
            </Link>
            <div className="text-right shrink-0">
              <div className="font-mono text-sm font-semibold text-brand-navy mb-1">
                {formatPrice(p.retail_price_cents)}
              </div>
              <button
                type="button"
                onClick={() => onAdd(p)}
                data-testid={`cart-rec-add-${p.slug}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-white text-brand-navy border border-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
              >
                <Plus size={10} weight="bold" /> Add
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Cart() {
  useNoIndex("/cart", {
    title: "Your Cart",
    description: "Review the research reference standards in your Medical Peptide Store cart. Prices and shipping quote confirmed at secure Stripe checkout.",
  });
  const { items, remove, update, add, subtotal, issues, refresh } = useCart();
  const nav = useNavigate();
  const [config, setConfig] = useState({ commerce_mode: "catalog_only" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config/public").then((r) => setConfig(r.data));
  }, []);

  // view_cart fires once per Cart mount with the loaded cart state.
  useEffect(() => {
    if (items && items.length) {
      trackViewCart({ items, subtotal_cents: subtotal, currency: "USD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // Phase 28n — shipping price is quoted LIVE on /checkout/shipping.
  // We no longer show a "shipping" line on the cart; the customer will see
  // carrier + amount + ETA on the next page before they hit Stripe.
  const shippingCents = 0;
  const grand = subtotal + shippingCents;

  const checkout = async () => {
    if (items.length === 0) return;
    setBusy(true);
    try {
      await refresh();
      nav("/checkout/shipping");
    } catch (_) {
      try { trackCheckoutError({ error_stage: "cart", error_code: "network_error" }); } catch (__) {}
    } finally {
      setBusy(false);
    }
  };

  const isCatalogMode = config.commerce_mode === "catalog_only";
  const isMaintenance = config.commerce_mode === "maintenance";
  const disabled = isCatalogMode || isMaintenance || items.length === 0;
  const hasBlockingIssue = issues.some((i) => ["PRODUCT_UNAVAILABLE", "NOT_CHECKOUT_ELIGIBLE", "INSUFFICIENT_STOCK", "INVALID_PRICE"].includes(i.code));

  return (
    <div className="bg-brand-surface min-h-screen" data-testid="cart-page" data-clarity-mask="true">
      <div className="mps-container py-14 md:py-20">
        <div className="max-w-2xl mb-8">
          <div className="eyebrow mb-4">Cart</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">Your cart</h1>
          <p className="mt-3 text-brand-muted">Review your items and continue to secure Stripe checkout.</p>
        </div>

        {items.length > 0 && (
          <div className="mb-8">
            <CheckoutStageCue />
          </div>
        )}

        {items.length === 0 ? (
          <div className="mps-card p-16 lg:p-24 text-center" data-testid="cart-empty">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-surface border border-brand-border mb-6">
              <Flask size={22} weight="regular" className="text-brand-navy" />
            </div>
            <div className="font-display text-2xl font-semibold text-brand-navy">Your cart is empty.</div>
            <p className="mt-2 text-brand-muted">Browse the catalog to add research peptides to your cart.</p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-[14px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold transition-colors"
            >
              Browse the Catalog <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8 space-y-5">
              <div className="flex items-center justify-between">
                <Link
                  to="/shop"
                  data-testid="cart-continue-shopping"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy hover:text-brand-accent transition-colors"
                >
                  ← Continue shopping
                </Link>
                <span className="text-xs text-brand-muted font-medium">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </span>
              </div>

              {issues.length > 0 && (
                <div className="rounded-[14px] border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="cart-issues">
                  <div className="flex items-center gap-2 font-semibold text-amber-800 mb-2">
                    <WarningCircle size={16} weight="regular" /> Cart updated during revalidation
                  </div>
                  <ul className="list-disc pl-6 space-y-1">
                    {issues.map((iss) => (<li key={`${iss.code}:${iss.product_id || iss.message}`} data-testid={`cart-issue-${iss.code}`}>{iss.message}</li>))}
                  </ul>
                </div>
              )}

              <div className="mps-card divide-y divide-brand-border p-0 overflow-hidden">
                {items.map((it) => (
                  <div key={it.product_id} className="p-5 sm:p-6 flex flex-wrap items-center gap-3 sm:gap-4">
                    {it.thumbnail_image_url && (
                      <img
                        src={imgUrl(it.thumbnail_image_url)}
                        alt={it.image_alt_text || it.public_display_name}
                        className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-brand-surface border border-brand-border rounded-[12px] p-2 shrink-0"
                        loading="lazy"
                      />
                    )}
                    <div className="flex-1 min-w-[10rem]">
                      <div className="font-display font-semibold text-brand-navy leading-snug line-clamp-2">{it.public_display_name}</div>
                      <Link to={`/product/${it.slug}`} className="text-sm text-brand-muted hover:text-brand-navy mt-1 inline-block">View details · {it.strength}</Link>
                    </div>
                    <div className="flex items-center rounded-[10px] border border-brand-border bg-white overflow-hidden order-3 sm:order-none">
                      <button onClick={() => update(it.product_id, Math.max(1, it.quantity - 1))} className="px-3 py-2 text-brand-text hover:bg-brand-surface transition-colors" aria-label="Decrease quantity" data-testid={`cart-dec-${it.slug}`}>−</button>
                      <span className="px-4 py-2 font-semibold text-brand-navy min-w-[3rem] text-center" data-testid={`cart-qty-${it.slug}`}>{it.quantity}</span>
                      <button onClick={() => update(it.product_id, it.quantity + 1)} className="px-3 py-2 text-brand-text hover:bg-brand-surface transition-colors" aria-label="Increase quantity" data-testid={`cart-inc-${it.slug}`}>+</button>
                    </div>
                    <div className="w-20 sm:w-28 text-right font-semibold text-brand-navy text-lg" data-testid={`cart-line-total-${it.slug}`}>{formatPrice(it.unit_amount_cents * it.quantity)}</div>
                    <button onClick={() => remove(it.product_id)} className="text-brand-muted hover:text-red-600 p-1 transition-colors" data-testid={`cart-remove-${it.slug}`} aria-label="Remove">
                      <Trash size={18} weight="regular" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Research-use-only notice — visible but NON-BLOCKING. */}
              <div className="mps-card p-6 lg:p-7" data-testid="cart-ruo-notice">
                <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-accent mb-3">Research-use notice</div>
                <p className="text-sm text-brand-muted leading-relaxed">
                  Every item in this cart is a research reference standard — <strong className="text-brand-navy">for research use only</strong>, <strong className="text-brand-navy">not for human or veterinary use</strong>, and not intended to diagnose, treat, cure, or prevent any disease. All shipments require a signature at delivery and are final sale except as required by law.
                </p>
                <p className="mt-3 text-xs text-brand-muted">
                  By continuing you acknowledge our <Link to="/research-use-policy" className="underline hover:text-brand-navy" data-testid="cart-ruo-link">Research-Use Policy</Link>, <Link to="/terms" className="underline hover:text-brand-navy" data-testid="cart-terms-link">Terms</Link>, and <Link to="/returns" className="underline hover:text-brand-navy" data-testid="cart-returns-link">Final-Sale &amp; Claims</Link> policy. No acceptance click is required.
                </p>
              </div>

              {/* Optional recommendations — max 3, positioned before the
                  order summary, honest, explicit Add per row. */}
              <CartRecommendations
                cartItems={items}
                onAdd={async (p) => {
                  try {
                    await add(p, 1, { openDrawer: false });
                    toast.success(`${p.product_name} added to cart`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Could not add to cart");
                  }
                }}
              />
            </div>

            {/* Order summary — sticky right rail */}
            <div className="lg:col-span-4">
              <div className="mps-card p-7 lg:p-8 sticky top-32">
                <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-muted mb-5">Order Summary</div>

                <div className="space-y-3 text-sm">
                  <Row label="Subtotal" value={formatPrice(subtotal)} testid="cart-subtotal" />
                  <Row label="Shipping" value="Calculated at checkout" muted testid="cart-shipping" />
                  <Row label="Tax" value="Calculated at checkout" muted />
                </div>

                <div className="border-t border-brand-border my-5" />
                <div className="flex justify-between items-baseline mb-6">
                  <span className="font-display text-lg font-semibold text-brand-navy">Total</span>
                  <span className="font-display font-bold text-2xl text-brand-navy" data-testid="cart-total">{formatPrice(grand)}</span>
                </div>

                {isCatalogMode && (
                  <div className="mb-4 rounded-[12px] border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="catalog-mode-notice">
                    Catalog · Review Mode — live checkout is disabled.
                  </div>
                )}
                {isMaintenance && (
                  <div className="mb-4 rounded-[12px] border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="maintenance-notice">
                    Checkout is temporarily unavailable for maintenance.
                  </div>
                )}
                {hasBlockingIssue && (
                  <div className="mb-4 rounded-[12px] border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="cart-blocking-issue">
                    Resolve cart issues above before continuing.
                  </div>
                )}

                <button
                  onClick={checkout}
                  disabled={busy || disabled || hasBlockingIssue}
                  data-testid="cart-checkout-btn"
                  className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-[14px] bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold text-base transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px] hover:shadow-soft disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:transform-none disabled:hover:shadow-none"
                >
                  {disabled ? "Checkout unavailable" : (busy ? "Redirecting…" : "Continue to Secure Checkout")} {!disabled && !busy && <ArrowRight size={16} weight="bold" />}
                </button>

                <div className="mt-5 space-y-2.5 text-xs text-brand-muted">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} weight="regular" className="text-brand-accent" />
                    Prices are checked securely before payment
                  </div>
                  <div className="flex items-center gap-2">
                    <PenNib size={14} weight="regular" className="text-brand-accent" />
                    Signature required at delivery
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function Row({ label, value, testid, muted = false }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-brand-muted">{label}</span>
      <span className={muted ? "text-brand-muted" : "font-semibold text-brand-text"} data-testid={testid}>{value}</span>
    </div>
  );
}
